import React, { useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';
import { Card } from 'antd';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import 'moment/locale/pt-br';
import { 
  Board, 
  Container, 
  Column, 
  ColumnContent, 
  ExtraContainer,
  ExtraTotalBadge,
  LoadingSkeletonContainer,
  SkeletonLine
} from './styles';
import MarketingCard from '../MarketingCard';
import Header from '../Header';
import MarketingSummary from '../Summary';
import { useMarketingFiltering } from '../../hooks/useMarketingFiltering';
import * as drawerBoardActions from '../Drawer/actions';
import * as boardActions from '../../actions';
import { BOARD_TYPES } from '../../constants';

const MarketingBoard = ({
  isLoading,
  date,
  setDate
}) => {
  const dispatch = useDispatch();
  const marketing = useSelector(state => state.marketing);
  const board = marketing.data ? marketing.data[1] : null;
  const isCurrentPeriod = marketing.currentPeriod ? marketing.currentPeriod.isCurrent : true;
  
  const handleDateChange = (newDate) => {
    // newDate comes as MM-YYYY from Header component
    setDate(newDate);
  };
  
  // Use the filtering hook
  const {
    searchQuery,
    setSearchQuery,
    filteredLists,
    searchResultsCount,
  } = useMarketingFiltering(board);
  
  const lists = filteredLists;

  const onDragEnd = result => {
    // Disable drag and drop for historical periods
    if (!isCurrentPeriod) {
      return;
    }
    
    if (!result.destination) {
      return;
    }
    if (result.source.droppableId === result.destination.droppableId) {
      // dispatch(boardActions.reorderList(result, BOARD_TYPES.MISSOES));
    } else {
      dispatch(boardActions.moveItem(result, BOARD_TYPES.MISSOES));
    }
  };

  if (!board) {
    return null;
  }

  const totalCounts = useMemo(() => {
    const counts = {};
    Object.keys(lists).forEach(listId => {
      counts[listId] = (lists[listId].cards || []).length;
    });
    return counts;
  }, [lists]);

  return (
    <div>
      <Header
        title={board.name || 'Missões'}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchResultsCount={searchResultsCount}
        onAddItem={() => {
          dispatch(boardActions.setBoardType(BOARD_TYPES.MISSOES));
          dispatch(drawerBoardActions.show());
        }}
        selectedDate={date}
        onDateChange={handleDateChange}
        showDateNavigation={true}
      />
      <div style={{ margin: '0 -16px 16px -16px', padding: '0 16px' }}>
        <MarketingSummary />
      </div>
      <Board>
        <Container>
          <DragDropContext onDragEnd={onDragEnd}>
            {Object.keys(lists).map((listId) => {
              const isCompletedColumn = parseInt(listId) === 3;
              const isColumnDisabled = !isCurrentPeriod && !isCompletedColumn;
              
              return (
                <Column key={listId}>
                  <Droppable droppableId={String(listId)} isDropDisabled={isColumnDisabled}>
                    {(provided) => (
                      <Card
                        title={lists[listId].name}
                        bodyStyle={{ padding: 0, height: "100%" }}
                        headStyle={lists[listId].style}
                        style={{ 
                          height: "100%", 
                          display: "flex", 
                          flexDirection: "column",
                          opacity: isColumnDisabled ? 0.5 : 1,
                          pointerEvents: isColumnDisabled ? 'none' : 'auto'
                        }}
                        extra={
                          <ExtraContainer>
                            <ExtraTotalBadge
                              count={totalCounts[listId] || 0}
                              overflowCount={9999}
                            />
                          </ExtraContainer>
                        }
                      >
                      <ColumnContent 
                        ref={provided.innerRef}
                        style={{
                          overflowY: isLoading ? "hidden" : "auto",
                        }}
                        {...provided.droppableProps}
                      >
                        {isLoading
                          ? Array(6)
                              .fill(1)
                              .map((_, index) => (
                                <LoadingSkeletonContainer key={index}>
                                  <SkeletonLine height="14px" width="80%" />
                                  <SkeletonLine height="12px" width="60%" />
                                  <SkeletonLine height="10px" width="40%" />
                                </LoadingSkeletonContainer>
                              ))
                          : (lists[listId].cards || []).map((item, index) => (
                              <MarketingCard
                                key={item.uid}
                                index={index}
                                item={item}
                                showDrawer={isColumnDisabled ? undefined : (item) => {
                                  dispatch(boardActions.setBoardType(BOARD_TYPES.MISSOES));
                                  dispatch(drawerBoardActions.show(item));
                                }}
                              />
                            ))}
                        {provided.placeholder}
                      </ColumnContent>
                    </Card>
                  )}
                </Droppable>
              </Column>
              );
            })}
          </DragDropContext>
        </Container>
      </Board>
    </div>
  );
};

export default MarketingBoard;